import { FC, useEffect, useState } from 'react';
import { Dayjs } from 'dayjs';
import { Box, MenuItem, SxProps, Theme, Typography } from '@mui/material';
import Select from '../../components/Select';
import DatePicker from '../../components/DatePicker';
import { FIELD_SX } from '../../components/fieldStyles';
import { colors } from '../../theme/tokens';
import { DocumentFilters, FilterOption } from './types';

// The three controls of the XD filter row besides the search box:
// "Archiveur", "Services" and the dates. They are painted inline on desktop and
// stacked inside the bottom sheet on mobile (ROADMAP.md, Part 2), with the
// same component so that they do not diverge.
//
// Dates: the XD draws a single range picker per column ("1 févr. 2026 - 31
// mars 2026"), which the listing API now filters by directly. There is no
// MUI `DateRangePicker` here: that control is part of MUI X Pro, which the
// project does not license. Each range is instead two independent
// `DatePicker`s with a dash between — the same pattern the Recherche
// dialog's "Entre" operator already uses (Search/DateCriterionFields) — under
// one `role="group"` label, since a floating label per side would be
// meaningless split across two fields.

type Props = {
  value: DocumentFilters;
  onChange: (patch: Partial<DocumentFilters>) => void;
  archiverOptions: FilterOption[];
  serviceOptions: FilterOption[];
  /** Stacked at full width (bottom sheet) or inline (desktop). */
  stacked?: boolean;
};

const CONTROL_WIDTH = 200;
// Half of CONTROL_WIDTH, roughly: a range's two sides sit next to each
// other on desktop, so neither can claim a full standalone field's width.
const RANGE_FIELD_WIDTH = 128;

// XD: the icons inside the filters (dropdown arrows, calendars) are blue.
const FILTER_ICON_SX = {
  '& .MuiSelect-icon, & .MuiSvgIcon-root, & .MuiIconButton-root': {
    color: colors.primary,
  },
} as const;

const selectSx = (stacked: boolean): SxProps<Theme> => ({
  ...(FIELD_SX as object),
  ...FILTER_ICON_SX,
  width: stacked ? '100%' : CONTROL_WIDTH,
});

const pickerSx = (stacked: boolean, width: number): SxProps<Theme> => ({
  ...(FIELD_SX as object),
  ...FILTER_ICON_SX,
  width: stacked ? '100%' : width,
});

// With `displayEmpty`, the empty value ("Tous") is painted with the filter
// name in grey, which is how the XD shows the dropdowns at rest.
const renderSelected =
  (placeholder: string, options: FilterOption[]) =>
  (selected: unknown) => {
    if (!selected) {
      return <span style={{ color: colors.hint }}>{placeholder}</span>;
    }
    const found = options.find((option) => option.value === selected);
    return found ? found.label : String(selected);
  };

// One side of a date range. The DatePicker's `onAccept` only fires from the
// calendar; a typed date only goes through `onChange` (and on every
// keystroke). It is kept in a draft and applied when it is a complete and
// valid date and the field is left or Enter is pressed; clearing it is
// applied instantly.
//
// `label` is a floating MUI label for a standalone field (unused inside
// `DateRangeField`, whose two sides share one group label instead —
// `aria-label` names each side there without drawing a second visible one).
type DateFieldProps = {
  id?: string;
  label?: string;
  ariaLabel?: string;
  value: Dayjs | null;
  onApply: (date: Dayjs | null) => void;
  stacked: boolean;
  width: number;
};

const DateField: FC<DateFieldProps> = ({
  id,
  label,
  ariaLabel,
  value,
  onApply,
  stacked,
  width,
}) => {
  const [draft, setDraft] = useState<Dayjs | null>(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const commitDraft = () => {
    if (draft === null) {
      if (value !== null) onApply(null);
      return;
    }
    if (!draft.isValid()) return;
    if (value && value.isSame(draft, 'day')) return;
    onApply(draft);
  };

  return (
    <DatePicker
      label={label}
      value={draft}
      onChange={(date) => {
        setDraft(date);
        if (date === null) onApply(null);
      }}
      onAccept={(date) => onApply(date)}
      slotProps={{
        textField: {
          id,
          size: 'small',
          placeholder: label ? undefined : 'JJ/MM/AAAA',
          inputProps: ariaLabel ? { 'aria-label': ariaLabel } : undefined,
          sx: pickerSx(stacked, width),
          onBlur: commitDraft,
          onKeyDown: (e) => {
            if (e.key === 'Enter') commitDraft();
          },
        },
        field: {
          clearable: true,
          onClear: () => onApply(null),
        },
      }}
    />
  );
};

// A date range: two DateFields with a dash between, under one group label.
// No MUI `DateRangePicker` (see the header note) — this is Search's
// "Entre" pattern (DateCriterionFields), fitted for a bare range with
// neither an operator nor a FormRow to carry the label.
type DateRangeFieldProps = {
  /** Ids: `${idPrefix}-from` / `${idPrefix}-to`, as DateCriterionFields does. */
  idPrefix: string;
  label: string;
  from: Dayjs | null;
  to: Dayjs | null;
  onApplyFrom: (date: Dayjs | null) => void;
  onApplyTo: (date: Dayjs | null) => void;
  stacked: boolean;
};

const DateRangeField: FC<DateRangeFieldProps> = ({
  idPrefix,
  label,
  from,
  to,
  onApplyFrom,
  onApplyTo,
  stacked,
}) => (
  <Box
    role="group"
    aria-label={label}
    // Inline, the label floats above the field so the group is exactly as
    // tall as the dropdowns and the whole filter row centres on one line.
    sx={{
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5,
    }}
  >
    <Typography
      component="span"
      aria-hidden
      sx={{
        fontSize: '0.75rem',
        lineHeight: 1.2,
        color: colors.muted,
        ...(!stacked && { position: 'absolute', bottom: '100%', mb: 0.25 }),
      }}
    >
      {label}
    </Typography>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexDirection: stacked ? 'column' : 'row',
        width: stacked ? '100%' : 'auto',
      }}
    >
      <DateField
        id={`${idPrefix}-from`}
        ariaLabel={`${label}, à partir du`}
        value={from}
        onApply={onApplyFrom}
        stacked={stacked}
        width={RANGE_FIELD_WIDTH}
      />
      {!stacked && (
        <Typography component="span" aria-hidden sx={{ color: colors.muted }}>
          –
        </Typography>
      )}
      <DateField
        id={`${idPrefix}-to`}
        ariaLabel={`${label}, jusqu'au`}
        value={to}
        onApply={onApplyTo}
        stacked={stacked}
        width={RANGE_FIELD_WIDTH}
      />
    </Box>
  </Box>
);

const FilterFields: FC<Props> = ({
  value,
  onChange,
  archiverOptions,
  serviceOptions,
  stacked = false,
}) => (
  <>
    <Select
      size="small"
      displayEmpty
      value={value.archiver}
      onChange={(e) => onChange({ archiver: e.target.value as string })}
      renderValue={renderSelected('Archiveur', archiverOptions)}
      inputProps={{ 'aria-label': 'Archiveur' }}
      sx={selectSx(stacked)}
    >
      {archiverOptions.map((option) => (
        <MenuItem key={`archiver_${option.value}`} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </Select>

    <Select
      size="small"
      displayEmpty
      value={value.service}
      onChange={(e) => onChange({ service: e.target.value as string })}
      renderValue={renderSelected('Services', serviceOptions)}
      inputProps={{ 'aria-label': 'Services' }}
      sx={selectSx(stacked)}
    >
      {serviceOptions.map((option) => (
        <MenuItem key={`service_${option.value}`} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </Select>

    <Box
      sx={{
        display: 'flex',
        gap: stacked ? 2 : 1.5,
        flexDirection: stacked ? 'column' : 'row',
        width: stacked ? '100%' : 'auto',
      }}
    >
      <DateRangeField
        idPrefix="home-issuing"
        label="Émission"
        from={value.issuingDateFrom}
        to={value.issuingDateTo}
        onApplyFrom={(date) => onChange({ issuingDateFrom: date })}
        onApplyTo={(date) => onChange({ issuingDateTo: date })}
        stacked={stacked}
      />
      <DateRangeField
        idPrefix="home-archiving"
        label="Archivage"
        from={value.archivingDateFrom}
        to={value.archivingDateTo}
        onApplyFrom={(date) => onChange({ archivingDateFrom: date })}
        onApplyTo={(date) => onChange({ archivingDateTo: date })}
        stacked={stacked}
      />
    </Box>
  </>
);

export default FilterFields;
